import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';

export const PompeoCoppini = () => {
  const majorWorks = [
    {
      name: "The Alamo Cenotaph (Spirit of Sacrifice)",
      year: "1939",
      location: "Alamo Plaza, San Antonio, TX",
      description: "Perhaps his most famous work, this 60-foot monument honors the heroes of the Alamo including Jim Bowie, Davy Crockett, William B. Travis, and James Bonham.",
      image: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/7f/AlamoMemorial-0727.jpg/800px-AlamoMemorial-0727.jpg"
    },
    {
      name: "Littlefield Fountain",
      year: "1933",
      location: "University of Texas at Austin",
      description: "A magnificent fountain commemorating those who lost their lives in World War I, featuring dynamic bronze figures emerging from the water.",
      image: "https://upload.wikimedia.org/wikipedia/commons/thumb/4/4a/The_University_of_Texas_at_Austin_-_Littlefield_Fountain_and_Main_Building.jpg/800px-The_University_of_Texas_at_Austin_-_Littlefield_Fountain_and_Main_Building.jpg"
    },
    {
      name: "Lawrence Sullivan Ross",
      year: "1919",
      location: "Texas A&M University",
      description: "One of the most revered works on campus. Students place coins at the statue's feet for good luck on exams.",
      image: "https://upload.wikimedia.org/wikipedia/commons/thumb/4/48/TAMU_Sul_Ross_statue.jpg/400px-TAMU_Sul_Ross_statue.jpg"
    },
    {
      name: "Rufus C. Burleson",
      year: "1905",
      location: "Baylor University, Waco, TX",
      description: "A tribute to the former Baylor University President, showcasing excellent lost wax casting technique.",
      image: "https://upload.wikimedia.org/wikipedia/commons/thumb/9/95/Burleson.jpg/400px-Burleson.jpg"
    },
    {
      name: "Terry's Texas Rangers Monument",
      year: "1907",
      location: "Texas State Capitol, Austin",
      description: "Honoring the 8th Texas Cavalry Regiment, this dynamic monument captures the spirit of the legendary Rangers.",
      image: "https://upload.wikimedia.org/wikipedia/commons/thumb/b/bc/Texas_Ranger_monument_in_front_of_Texas_State_Capitol.JPG/400px-Texas_Ranger_monument_in_front_of_Texas_State_Capitol.JPG"
    },
    {
      name: "George Washington",
      year: "1955",
      location: "University of Texas at Austin",
      description: "The third of three Washington statues Coppini created, this one stands on the south mall of UT Austin.",
      image: "https://upload.wikimedia.org/wikipedia/commons/thumb/3/3e/Usa_texas_austin_budova_university_tower_2005.jpg/400px-Usa_texas_austin_budova_university_tower_2005.jpg"
    }
  ];

  const timeline = [
    { year: "1870", event: "Born in Moglia, Province of Mantua, Italy on May 19" },
    { year: "1886", event: "At age 16, enrolled at Florence's Accademia delle Arti del Disegno" },
    { year: "1896", event: "Emigrated to the United States with $40 and a trunk of clothes" },
    { year: "1897", event: "Worked on Fountain of Neptune at Library of Congress" },
    { year: "1901", event: "Moved to Texas to work with German sculptor Frank Teich" },
    { year: "1902", event: "Became a United States citizen" },
    { year: "1903", event: "Completed Confederate Soldiers Monument at Texas State Capitol" },
    { year: "1910", event: "Waldine Tauch became his student and protégée" },
    { year: "1933", event: "Completed the Littlefield Fountain at UT Austin" },
    { year: "1934", event: "Designed the Texas Centennial Half Dollar" },
    { year: "1939", event: "Completed the Alamo Cenotaph" },
    { year: "1941", event: "Received honorary Doctor of Fine Arts from Baylor University" },
    { year: "1945", event: "Founded the Coppini Academy of Fine Art in San Antonio" },
    { year: "1957", event: "Died on September 26 in San Antonio, buried in a crypt he designed" }
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="pompeo-coppini-page">
      <SEOHead
        title="Pompeo Luigi Coppini — Sculptor & Founder"
        description="Learn about Pompeo Luigi Coppini (1870–1957), Italian-born sculptor of the Alamo Cenotaph and founder of The Coppini Academy of Fine Art in San Antonio."
        canonicalPath="/about/pompeo-coppini"
      />
      {/* Hero */}
      <section className="bg-gradient-to-b from-stone-900 to-stone-800 text-white py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
            <div className="md:col-span-2">
              <p className="text-cafa-blue-300 font-medium mb-2">1870 - 1957</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif mb-6">Pompeo Luigi Coppini</h1>
              <p className="text-xl text-stone-300 leading-relaxed">
                Italian-American sculptor whose monumental works define the artistic landscape of Texas. 
                Creator of the Alamo Cenotaph and founder of the Coppini Academy of Fine Art.
              </p>
              <p className="text-stone-400 mt-4">
                <Link to="/about/history" className="text-cafa-blue-300 hover:text-cafa-blue-200 underline">
                  ← Back to Our History
                </Link>
              </p>
            </div>
            <div className="text-center">
              <img 
                src="https://upload.wikimedia.org/wikipedia/commons/thumb/1/1c/Pompeo_Coppini_%28cropped%29.jpg/400px-Pompeo_Coppini_%28cropped%29.jpg"
                alt="Pompeo Coppini in 1928"
                className="w-64 h-auto mx-auto rounded-lg shadow-2xl border-4 border-white/20"
              />
              <p className="text-sm text-stone-400 mt-3">Coppini in 1928</p>
            </div>
          </div>
        </div>
      </section>

      {/* Biography */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-stone-900 mb-8">Early Life & Journey to America</h2>
          
          <div className="prose prose-lg max-w-none text-stone-700">
            <p className="mb-6">
              Pompeo Luigi Coppini was born on May 19, 1870, in Moglia, a small community in the Province of Mantua, Italy. 
              His family moved to Florence, where at an early age, he was influenced by a neighborhood studio that created 
              copies of famous sculptures for tourists. At just 16 years old, he secured a place at Florence's renowned 
              <strong> Accademia delle Arti del Disegno</strong>, studying under master sculptor Augusto Rivalta and 
              graduating at the top of his class.
            </p>
            
            <p className="mb-6">
              After serving in the Italian army twice and trying his hand at various occupations—tutor, hydraulic engineer, 
              sign painter, waiter, and bookkeeper—he received a contract to work on an eight-foot marble angel destined 
              for a cemetery in Boston. In 1896, at 26 years old, he boarded a ship bound for New York with nothing but 
              <strong> $40 and a trunk of clothes</strong>.
            </p>

            <p className="mb-6">
              Historical accounts describe how he ran out of money fast, going six days without food and nearly 
              perishing before a kind Italian woman took pity on him. Yet despite these early hardships, when 
              Coppini came to America, he adopted it as his true home and was deeply dedicated to his new country. 
              He adored the place.
            </p>

            <p className="mb-6">
              In New York, Coppini worked at a local wax museum sculpting figures and collaborated with established sculptors 
              on major projects, including the Fountain of Neptune at the Library of Congress (1897). Through artist Alexander Doyle, 
              he worked on the Francis Scott Key memorial in Frederick, Maryland, where he met his wife, <strong>Elizabeth Maria Di Barbieri</strong>, 
              who modeled for the piece. They would remain married for nearly 60 years.
            </p>
          </div>
        </div>
      </section>

      {/* Texas Years */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-stone-900 mb-8">The Texas Years</h2>
          
          <div className="prose prose-lg max-w-none text-stone-700">
            <p className="mb-6">
              Frustrated by his lack of success in New York, Coppini accepted an opportunity in 1901 to work with 
              <strong> Frank Teich</strong>, a German-born stonecutter based in San Antonio. He became an American citizen 
              the following year. Coppini arrived in Texas during the City Beautiful movement, when American cities were 
              reforming their architecture to include parks with statues and civic monuments. It was a sculptor's dream.
            </p>

            <p className="mb-6">
              During the next 15 years, Coppini received numerous commissions to celebrate Texas heroes. His works were 
              installed at the Texas State Capitol in Austin, Texas A&M University, and throughout the state. He became 
              known for his ability to capture the spirit of heroic figures in bronze and marble.
            </p>

            <p className="mb-6">
              By 1910, <strong>Waldine Tauch</strong>—a young sculptor from Schulenburg, Texas—became Coppini's student, 
              then apprentice, and finally his partner. He mentored her in the classical tradition, and she collaborated 
              with him until his death. Tauch became a noted sculptor in her own right, carrying on his legacy.
            </p>
          </div>
        </div>
      </section>

      {/* Major Works Gallery */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-stone-900 mb-4 text-center">Major Works</h2>
          <p className="text-stone-600 text-center mb-12 max-w-2xl mx-auto">
            Coppini created over 230 works during his lifetime, including 76 publicly displayed statues 
            in 12 states and 3 countries. Here are some of his most celebrated pieces.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {majorWorks.map((work, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden border border-stone-200 hover:shadow-lg transition-shadow">
                <div className="h-56 overflow-hidden bg-stone-100">
                  <img 
                    src={work.image}
                    alt={work.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-cafa-red font-bold">{work.year}</span>
                    <span className="text-xs text-stone-500 bg-stone-100 px-2 py-1 rounded">{work.location}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-stone-900 mb-2">{work.name}</h3>
                  <p className="text-sm text-stone-600">{work.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Alamo Cenotaph Feature */}
      <section className="py-16 bg-stone-900 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-cafa-blue-300 font-medium mb-2">His Masterpiece</p>
              <h2 className="text-3xl font-serif mb-6">The Alamo Cenotaph</h2>
              <p className="text-stone-300 mb-6 leading-relaxed">
                Dedicated in November 1940, the Alamo Cenotaph—also known as "The Spirit of Sacrifice"—is 
                the work Coppini considered his greatest achievement. The 60-foot monument celebrates the 
                heroes of the Battle of the Alamo.
              </p>
              <blockquote className="border-l-4 border-cafa-blue pl-4 italic text-stone-400 mb-6">
                "I know I was chosen, not only for my artistic merits, but also because of my proven love 
                and almost fanatic admiration for the heroes of the Alamo."
                <footer className="text-cafa-blue-300 mt-2 not-italic">— Pompeo Coppini</footer>
              </blockquote>
              <p className="text-stone-300 leading-relaxed">
                The monument features heroic figures of Jim Bowie, James Bonham, Davy Crockett, and William B. Travis, 
                carved into triangular marble panels. Coppini was in his late sixties when he completed this 
                immense project, using a pneumatic drill to help carve the intricate figures.
              </p>
            </div>
            <div>
              <img 
                src="https://upload.wikimedia.org/wikipedia/commons/thumb/7/7f/AlamoMemorial-0727.jpg/600px-AlamoMemorial-0727.jpg"
                alt="The Alamo Cenotaph"
                className="w-full rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-16 bg-cafa-red-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-stone-900 mb-12 text-center">Life Timeline</h2>
          
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-cafa-blue-300"></div>
            
            <div className="space-y-8">
              {timeline.map((item, index) => (
                <div key={index} className="flex items-start gap-6 relative">
                  <div className="flex-shrink-0 w-16 text-right">
                    <span className="font-bold text-cafa-red">{item.year}</span>
                  </div>
                  <div className="flex-shrink-0 w-4 h-4 mt-1 rounded-full bg-cafa-red-500 border-4 border-white z-10"></div>
                  <p className="text-stone-700 flex-1 pb-4">{item.event}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* The Coppini Academy */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-stone-900 mb-8 text-center">The Coppini Academy Legacy</h2>
          
          <div className="prose prose-lg max-w-none text-stone-700 text-center">
            <p className="mb-6">
              In 1945, Coppini established the <strong>Coppini Academy of Fine Art</strong> at his studio 
              on Melrose Place in San Antonio, where he had completed work on the Cenotaph. Today, the Academy 
              continues his mission of promoting classical and representational art through educational programs, 
              demonstrations, and workshops.
            </p>
            
            <p className="mb-8">
              "When Texans think of their history, quite often the images they have in their mind are images 
              created by Papa Coppini," noted Dr. Rick McCaslin of the Texas State Historical Association.
            </p>
          </div>
          
          <div className="bg-stone-900 text-white p-8 rounded-xl text-center">
            <h3 className="text-2xl font-serif mb-4">Continue the Legacy</h3>
            <p className="text-stone-300 mb-6">
              The Coppini Academy of Fine Art continues Pompeo's vision today, offering classes in 
              classical and representational fine art.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/classes/adult"
                className="inline-flex items-center justify-center px-6 py-3 bg-cafa-red hover:bg-cafa-red-700 text-white rounded-md font-medium transition-colors"
              >
                Explore Our Classes
              </Link>
              <Link 
                to="/about"
                className="inline-flex items-center justify-center px-6 py-3 border border-white text-white hover:bg-white/10 rounded-md font-medium transition-colors"
              >
                About the Academy
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
